import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { AccountType } from '../lib/supabase';
import { NotificationPermissionBanner } from '../components/NotificationPermissionBanner';

const countryCodes = [
  { code: '+967', country: 'اليمن' },
  { code: '+249', country: 'السودان' },
  { code: '+20', country: 'مصر' },
  { code: '+966', country: 'السعودية' },
  { code: '+962', country: 'الأردن' },
  { code: '+968', country: 'عمان' },
  { code: '+964', country: 'العراق' },
  { code: '+963', country: 'سوريا' },
  { code: '+213', country: 'الجزائر' },
  { code: '+971', country: 'الإمارات' },
];

interface FormData {
  username: string;
  phone: string;
  password: string;
  confirmPassword: string;
  dateOfBirth: string;
  address: string;
  accountType: AccountType;
  referenceSmartKey: string;
  shopName?: string;
  businessType?: string;
}

function Register() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [countryCode, setCountryCode] = useState('+967');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [agreedToPrivacyPolicy, setAgreedToPrivacyPolicy] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [isInvitation, setIsInvitation] = useState(false);
  const [invitationInfo, setInvitationInfo] = useState<{
    shopOwner: string;
    shopName: string;
    shopOwnerSmartKey: string;
    invitationCode?: string;
  } | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState<FormData>({
    username: '',
    phone: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: '',
    address: '',
    accountType: 'customer',
    referenceSmartKey: '',
    shopName: '',
    businessType: '',
  });

  useEffect(() => {
    // Check if this is an invitation registration
    const params = new URLSearchParams(location.search);
    const phone = params.get('phone');
    const countryCodeParam = params.get('countryCode');
    const address = params.get('address');
    const shopOwner = params.get('shopOwner');
    const shopName = params.get('shopName');
    const referenceSmartKey = params.get('referenceSmartKey');
    const invitationCode = params.get('invitationCode');

    if (phone && countryCodeParam && shopOwner && referenceSmartKey) {
      setIsInvitation(true);
      setInvitationInfo({ 
        shopOwner, 
        shopName: shopName || '', 
        shopOwnerSmartKey: referenceSmartKey,
        invitationCode: invitationCode || undefined
      });
      setCountryCode(countryCodeParam);
      setFormData(prev => ({
        ...prev,
        phone,
        address: address || '',
        accountType: 'customer',
        referenceSmartKey
      }));
    }
  }, [location.search]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreedToPrivacyPolicy) {
      setError('يجب الموافقة على سياسة الخصوصية للمتابعة');
      return;
    }
    
    setError(null);
    setLoading(true);

    try {
      if (formData.password !== formData.confirmPassword) {
        throw new Error('كلمات المرور غير متطابقة');
      }

      if (formData.accountType === 'shop_owner' && (!formData.shopName || !formData.businessType)) {
        throw new Error('اسم المتجر ونوع النشاط مطلوبان لأصحاب المتاجر');
      }

      if (recoveryEmail && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(recoveryEmail)) {
        throw new Error('البريد الإلكتروني غير صالح');
      }

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: `${formData.phone}@ajel.life`,
        password: formData.password,
        phone: countryCode + formData.phone,
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('لم يتم إنشاء المستخدم');

      const { error: profileError } = await supabase
        .from('profiles')
        .insert({
          id: authData.user.id,
          username: formData.username,
          phone: formData.phone,
          country_code: countryCode,
          date_of_birth: formData.dateOfBirth,
          address: formData.address,
          account_type: formData.accountType,
          reference_smart_key: formData.referenceSmartKey || null,
          shop_name: formData.accountType === 'shop_owner' ? formData.shopName : null,
          business_type: formData.accountType === 'shop_owner' ? formData.businessType : null,
          assistant_vendor_smart_key: formData.accountType === 'shop_assistant' ? formData.referenceSmartKey : null,
          assistant_status: formData.accountType === 'shop_assistant' ? 'not_active' : null,
          recovery_email: recoveryEmail || null
        });

      if (profileError) {
        await supabase.auth.signOut();
        throw profileError;
      }

      // If this is an invitation registration, use the server-side function to create relationships
      if (isInvitation && invitationInfo?.invitationCode) {
        // Use the invitation code to create relationships
        try {
          console.log('Processing invitation code registration...');
          
          const { data: invitationResult, error: invitationError } = await supabase
            .rpc('use_invitation', {
              p_code: invitationInfo.invitationCode,
              p_customer_user_id: authData.user.id
            });

          if (invitationError) {
            console.error('Error using invitation:', invitationError);
          } else if (invitationResult?.success) {
            console.log('Invitation used successfully:', invitationResult);
          }
        } catch (invitationError) {
          console.error('Error in invitation processing:', invitationError);
        }
      } else if (isInvitation && formData.referenceSmartKey && formData.accountType === 'customer') {
        // Fallback to the old method for backwards compatibility
        try {
          console.log('Processing invitation registration for customer...');
          
          // Call the server-side function to handle the relationship creation
          const { data: relationshipResult, error: relationshipError } = await supabase
            .rpc('handle_invitation_registration', {
              p_customer_user_id: authData.user.id,
              p_vendor_smart_key: formData.referenceSmartKey
            });

          if (relationshipError) {
            console.error('Error creating vendor-customer relationship:', relationshipError);
            // Don't throw error here as registration was successful
            console.log('Registration completed but relationship creation failed:', relationshipError.message);
          } else {
            console.log('Vendor-customer relationship and quick access created successfully:', relationshipResult);
          }
        } catch (relationshipError) {
          console.error('Error in relationship creation process:', relationshipError);
          // Don't throw error here as registration was successful
        }
      }

      navigate('/home', { replace: true });
    } catch (err) {
      console.error('Registration error:', err);
      setError(err instanceof Error ? err.message : 'حدث خطأ أثناء التسجيل');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-purple-50 to-pink-50 p-6 flex flex-col items-center justify-center">
      {/* Notification Permission Banner */}
      <NotificationPermissionBanner />
      
      <div className="w-full max-w-sm bg-white rounded-3xl shadow-lg p-8 relative overflow-hidden border border-purple-100">
        {/* Decorative elements */}
        <div className="absolute -top-16 -right-16 w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full opacity-70"></div>
        <div className="absolute -bottom-16 -left-16 w-32 h-32 bg-gradient-to-tr from-pink-100 to-orange-100 rounded-full opacity-70"></div>
        
        <div className="relative text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full flex items-center justify-center">
              <span className="text-2xl">✨</span>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">
            {isInvitation ? 'إكمال التسجيل' : 'إنشاء حساب'}
          </h1>
          {isInvitation && invitationInfo ? (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 text-right">
              <div className="flex items-center justify-center mb-2">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-200 to-purple-200 rounded-full flex items-center justify-center">
                  <span className="text-2xl">🎉</span>
                </div>
              </div>
              <p className="text-blue-700 text-sm mb-2">
                تمت دعوتك من <span className="font-medium">{invitationInfo.shopOwner}</span>
                {invitationInfo.shopName && (
                  <span> من {invitationInfo.shopName}</span>
                )}
              </p>
              <div className="bg-blue-100 rounded-lg p-3 mb-2">
                <p className="text-blue-800 text-xs font-medium mb-1">الرمز الذكي للمتجر:</p>
                <p className="text-blue-900 font-mono text-sm font-bold">{invitationInfo.shopOwnerSmartKey}</p>
              </div>
              <p className="text-blue-600 text-xs">
                سيتم ربط حسابك تلقائياً بهذا المتجر عند إكمال التسجيل
              </p>
            </div>
          ) : (
            <p className="text-gray-600">أدخل بياناتك لإنشاء حسابك الان و استمتع بحساب متابعة مبيعاتك و مشترياتك الاجلة بكل الخصائص و المميزات مجانا</p>
          )}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm border border-red-100 shadow-sm">
            {error}
          </div>
        )}

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <input
              type="text"
              name="username"
              placeholder="اسم المستخدم"
              value={formData.username}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500"
              required
            />
          </div>

          <div>
            <select
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              disabled={isInvitation}
              className={`w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 ${
                isInvitation ? 'bg-gray-100' : 'bg-white'
              }`}
              required
            >
              {countryCodes.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.country} ({country.code})
                </option>
              ))}
            </select>
          </div>

          <div>
            <input
              type="tel"
              name="phone"
              placeholder="رقم الهاتف"
              value={formData.phone}
              onChange={handleChange}
              disabled={isInvitation}
              className={`w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 text-right ${
                isInvitation ? 'bg-gray-100' : ''
              }`}
              required
            />
            <p className="mt-1 text-xs text-gray-500">لن يضهر رقم هاتفك لاي مستخدم اخر تحت اي ضرف.</p>
          </div>

          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="كلمة المرور"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 text-right pl-12"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          <div className="relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="تأكيد كلمة المرور"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 text-right pl-12"
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          <div>
            <input
              type="date"
              name="dateOfBirth"
              placeholder="تاريخ الميلاد"
              value={formData.dateOfBirth}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500"
              required
            />
          </div>

          <div>
            <input
              type="text"
              name="address"
              placeholder="العنوان"
              value={formData.address}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500"
              required
            />
          </div>

          {!isInvitation && (
            <div>
              <select 
                name="accountType"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 bg-white"
                value={formData.accountType}
                onChange={handleChange}
                required
              >
                <option value="customer">عميل</option>
                <option value="shop_owner">صاحب متجر</option>
                <option value="shop_assistant">مساعد متجر</option>
              </select>
            </div>
          )}

          {isInvitation && (
            <div>
              <input
                type="text"
                value="عميل"
                disabled
                className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-100"
              />
            </div>
          )}

          {!isInvitation && (
            <div>
              <input
                type="text"
                name="referenceSmartKey"
                placeholder="الرمز الذكي لمن عرفك على النظام (اختياري)"
                value={formData.referenceSmartKey}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 uppercase"
                maxLength={6}
              />
            </div>
          )}

          {isInvitation && (
            <div className="p-3 bg-gray-50 rounded-xl">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                الرمز الذكي للمتجر المرتبط
              </label>
              <input
                type="text"
                value={formData.referenceSmartKey}
                disabled
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-100 font-mono text-center font-bold text-blue-800"
              />
            </div>
          )}

          {formData.accountType === 'shop_assistant' && !isInvitation && (
            <div>
              <input
                type="text"
                name="referenceSmartKey"
                placeholder="الرمز الذكي لصاحب المتجر"
                value={formData.referenceSmartKey}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 uppercase"
                maxLength={6}
                required
              />
            </div>
          )}

          {formData.accountType === 'shop_owner' && (
            <>
              <div>
                <input
                  type="text"
                  name="shopName"
                  placeholder="اسم المتجر"
                  value={formData.shopName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <input
                  type="text"
                  name="businessType"
                  placeholder="نوع النشاط التجاري"
                  value={formData.businessType}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500"
                  required
                />
              </div>
            </>
          )}

          <div>
            <input
              type="email"
              placeholder="البريد الإلكتروني لاستعادة كلمة المرور (اختياري)"
              value={recoveryEmail}
              onChange={(e) => setRecoveryEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500"
            />
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="privacyPolicy"
              checked={agreedToPrivacyPolicy}
              onChange={(e) => setAgreedToPrivacyPolicy(e.target.checked)}
              className="w-4 h-4 text-blue-500 rounded border-gray-300 focus:ring-blue-500"
            />
            <label htmlFor="privacyPolicy" className="text-sm text-gray-600">
              أوافق على{' '}
              <button
                type="button"
                onClick={() => setShowPrivacyPolicy(true)}
                className="text-blue-500 hover:text-blue-600"
              >
                سياسة الخصوصية
              </button>
            </label>
          </div>

          <button
            type="submit"
            disabled={loading || !agreedToPrivacyPolicy}
            className="w-full py-3 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white rounded-xl hover:opacity-90 transition-all transform hover:scale-[1.02] shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {loading ? 'جاري إنشاء الحساب...' : isInvitation ? 'إكمال التسجيل' : 'تسجيل'}
          </button>
        </form>

        {!isInvitation && (
          <p className="mt-8 text-center text-sm text-gray-500">
            لديك حساب بالفعل؟{' '}
            <button
              onClick={() => navigate('/')} 
              className="font-medium text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 hover:opacity-80"
            >
              تسجيل الدخول
            </button>
          </p>
        )}
        
        {/* Celebratory bottom element */}
        <div className="mt-8 p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-purple-100 shadow-sm">
          <p className="text-center text-sm text-blue-800">
            انضم إلى أكثر من 100,000+ مستخدم يديرون أعمالهم بسهولة مع آجل 🚀
          </p>
        </div>
      </div>

      {/* Privacy Policy Modal */}
      {showPrivacyPolicy && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white w-full max-w-2xl rounded-2xl p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">سياسة الخصوصية</h2>
              <button 
                onClick={() => setShowPrivacyPolicy(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="prose prose-sm max-w-none">
              <p className="text-sm text-gray-600 mb-4">تاريخ السريان: 01/02/2025</p>

              <p className="mb-4">
                Ajel.life ("نحن" أو "لنا") ملتزمون بحماية خصوصيتك. توضح سياسة الخصوصية هذه كيفية جمع واستخدام والكشف عن وحماية معلوماتك عند استخدام تطبيق الويب الخاص بنا ("التطبيق").
              </p>

              <h3 className="text-lg font-semibold mb-2">المعلومات التي نجمعها</h3>
              <p className="mb-4">قد نجمع المعلومات التالية منك:</p>
              <ul className="list-disc list-inside mb-4">
                <li>عنوان البريد الإلكتروني</li>
                <li>رقم الهاتف المحمول</li>
                <li>اسم المستخدم</li>
              </ul>

              <h3 className="text-lg font-semibold mb-2">كيف نستخدم معلوماتك</h3>
              <p className="mb-2">نستخدم المعلومات التي نجمعها للأغراض التالية:</p>
              <ul className="list-disc list-inside mb-4">
                <li>إنشاء وإدارة حسابات المستخدمين</li>
                <li>التواصل معك، بما في ذلك إرسال التحديثات والإشعارات</li>
                <li>تحسين خدماتنا وتجربة المستخدم</li>
                <li>تقديم دعم العملاء</li>
              </ul>

              <h3 className="text-lg font-semibold mb-2">مشاركة المعلومات</h3>
              <p className="mb-2">
                نحن لا نبيع أو نؤجر أو نشارك معلوماتك الشخصية مع أطراف ثالثة إلا في الحالات التالية:
              </p>
              <ul className="list-disc list-inside mb-4">
                <li>بموافقتك</li>
                <li>لأسباب قانونية، مثل الامتثال للالتزامات القانونية أو الاستجابة للطلبات القانونية أو حماية حقوقنا</li>
                <li>مع مقدمي الخدمات الذين يقدمون خدمات نيابة عنا بموجب اتفاقيات سرية صارمة</li>
              </ul>

              <h3 className="text-lg font-semibold mb-2">أمن البيانات</h3>
              <p className="mb-4">
                نقوم بتنفيذ تدابير تقنية وتنظيمية مناسبة لحماية معلوماتك الشخصية من الوصول غير المصرح به أو الاستخدام أو الكشف أو التعديل أو التدمير.
              </p>

              <h3 className="text-lg font-semibold mb-2">حقوقك</h3>
              <p className="mb-2">لديك الحق في:</p>
              <ul className="list-disc list-inside mb-4">
                <li>الوصول إلى معلوماتك الشخصية أو تحديثها أو حذفها</li>
                <li>سحب موافقتك في أي وقت (عند الاقتضاء)</li>
                <li>الاتصال بنا بخصوص أي مخاوف تتعلق بالخصوصية</li>
              </ul>

              <h3 className="text-lg font-semibold mb-2">التغييرات في سياسة الخصوصية</h3>
              <p className="mb-4">
                قد نقوم بتحديث سياسة الخصوصية هذه من وقت لآخر. سيتم نشر أي تغييرات على هذه الصفحة مع تاريخ سريان محدث.
              </p>

              <h3 className="text-lg font-semibold mb-2">اتصل بنا</h3>
              <p className="mb-4">
                إذا كان لديك أي أسئلة أو مخاوف بشأن سياسة الخصوصية هذه، يرجى الاتصال بنا على:
              </p>
              <p>البريد الإلكتروني: ajel.life@gmail.com</p>

              <p className="mt-6 text-sm text-gray-500">Ajel.life</p>
              <p className="text-sm text-gray-500">01/02/2025</p>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowPrivacyPolicy(false)}
                className="px-4 py-2 bg-[#F87171] text-white rounded-xl hover:bg-[#EF4444] transition-colors"
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Register;